import React, { useState } from "react";
import axios from "axios";
import { ArrowUpRight, CheckCircle2, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
    "Educational",
    "Industrial",
    "Medical",
    "Office & Retail",
    "Religious",
    "University & College",
    "Nonprofit",
];

export const QuoteForm = ({ source = "quote_form_homepage" }) => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        message: "",
    });
    const [status, setStatus] = useState("idle");
    const [error, setError] = useState("");

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        if (!form.name.trim()) return setError("Name is required.");
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return setError("Valid email required.");

        setStatus("loading");
        try {
            await axios.post(`${API}/leads`, { ...form, source });
            if (typeof window.fbq === "function") {
                window.fbq("track", "Lead");
            }
            setStatus("success");
            setForm({ name: "", email: "", phone: "", project_type: "", message: "" });
        } catch (err) {
            console.error(err);
            setStatus("error");
            setError("Submission failed. Please try again or call us directly.");
        }
    };

    if (status === "success") {
        return (
            <div
                data-testid="quote-form-success"
                className="border border-foreground bg-card p-10 md:p-14"
            >
                <CheckCircle2 className="w-10 h-10 text-accent mb-4" />
                <h3 className="font-display font-black text-3xl uppercase tracking-tight">
                    Bid request received.
                </h3>
                <p className="mt-3 text-muted-foreground max-w-md">
                    Thank you. A member of our preconstruction team will reach out to discuss
                    scope, schedule, and budget.
                </p>
                <button
                    data-testid="quote-form-reset"
                    onClick={() => setStatus("idle")}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-foreground hover:text-accent"
                >
                    Submit another <ArrowUpRight className="w-4 h-4" />
                </button>
            </div>
        );
    }

    return (
        <form
            data-testid="quote-form"
            onSubmit={handleSubmit}
            className="border border-border bg-card p-6 md:p-10"
        >
            <div className="grid md:grid-cols-2 gap-px bg-border">
                <Field label="Name" required>
                    <input
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="quote-input-name"
                        placeholder="Jane Doe"
                        className="w-full bg-card px-4 py-4 focus:outline-none focus:ring-2 focus:ring-accent text-foreground placeholder:text-muted-foreground"
                    />
                </Field>
                <Field label="Email" required>
                    <input
                        type="email"
                        value={form.email}
                        onChange={update("email")}
                        data-testid="quote-input-email"
                        placeholder="you@company.com"
                        className="w-full bg-card px-4 py-4 focus:outline-none focus:ring-2 focus:ring-accent text-foreground placeholder:text-muted-foreground"
                    />
                </Field>
                <Field label="Phone">
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="quote-input-phone"
                        placeholder="(559) 555-0100"
                        className="w-full bg-card px-4 py-4 focus:outline-none focus:ring-2 focus:ring-accent text-foreground placeholder:text-muted-foreground"
                    />
                </Field>
                <Field label="Project Type">
                    <select
                        value={form.project_type}
                        onChange={update("project_type")}
                        data-testid="quote-input-project-type"
                        className="w-full bg-card px-4 py-4 focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
                    >
                        <option value="">Select a category...</option>
                        {PROJECT_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                </Field>
                <div className="md:col-span-2">
                    <Field label="Project Details">
                        <textarea
                            rows={5}
                            value={form.message}
                            onChange={update("message")}
                            data-testid="quote-input-message"
                            placeholder="Tell us about scope, square footage, target schedule, and any specific requirements..."
                            className="w-full bg-card px-4 py-4 focus:outline-none focus:ring-2 focus:ring-accent text-foreground placeholder:text-muted-foreground resize-none"
                        />
                    </Field>
                </div>
            </div>

            <div className="mt-6 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    Confidential · No obligation
                </p>
                <button
                    type="submit"
                    data-testid="quote-form-submit"
                    disabled={status === "loading"}
                    className="group inline-flex items-center justify-center gap-2 bg-foreground text-background px-8 py-4 font-bold uppercase tracking-wider hover:bg-accent hover:text-accent-foreground transition-colors disabled:opacity-60"
                >
                    {status === "loading" ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" /> Submitting
                        </>
                    ) : (
                        <>
                            Connect with MWC
                            <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </>
                    )}
                </button>
            </div>
            {error && (
                <p data-testid="quote-form-error" className="mt-3 text-sm text-destructive">
                    {error}
                </p>
            )}
        </form>
    );
};

const Field = ({ label, required, children }) => (
    <label className="block bg-card p-4">
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            {label}
            {required && <span className="text-accent"> *</span>}
        </span>
        <div className="mt-2 -mx-4 -mb-4">
            {React.Children.map(children, (child) =>
                React.cloneElement(child, {
                    className: (child.props.className || "") + " border-0",
                })
            )}
        </div>
    </label>
);

export default QuoteForm;
